// src/components/study/SavedToast.tsx
// ✅ Tema: lê data-theme do document para aplicar cores corretas
// Como o portal renderiza no body (fora do contexto React),
// a forma mais simples é ler document.documentElement ou body
// e passar uma classe ao toast.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bookmark, X } from 'lucide-react';
import './SavedToast.css';

interface SavedToastProps {
    isVisible: boolean;
    savedCardId: string | null;
    onOrganize: (savedCardId: string) => void;
    onClose: () => void;
    duration?: number;
}

const SavedToast: React.FC<SavedToastProps> = ({
    isVisible,
    savedCardId,
    onOrganize,
    onClose,
    duration = 4000
}) => {
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; });

    // ✅ Detecta o tema atual e observa mudanças
    const [isDark, setIsDark] = useState(() => {
        // Verifica data-theme em qualquer ancestral comum (html, body, ou .study-page-final)
        const html = document.documentElement;
        const body = document.body;
        const studyPage = document.querySelector('.study-page-final');
        return (
            html.getAttribute('data-theme') === 'dark' ||
            body.getAttribute('data-theme') === 'dark' ||
            studyPage?.getAttribute('data-theme') === 'dark' ||
            html.classList.contains('dark') ||
            body.classList.contains('dark')
        );
    });

    useEffect(() => {
        const checkTheme = () => {
            const html = document.documentElement;
            const body = document.body;
            const studyPage = document.querySelector('.study-page-final');
            setIsDark(
                html.getAttribute('data-theme') === 'dark' ||
                body.getAttribute('data-theme') === 'dark' ||
                studyPage?.getAttribute('data-theme') === 'dark' ||
                html.classList.contains('dark') ||
                body.classList.contains('dark')
            );
        };

        // MutationObserver detecta quando data-theme muda em tempo real
        const observer = new MutationObserver(checkTheme);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
        observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme', 'class'] });

        // Observa o .study-page-final também (onde data-theme é aplicado no seu código)
        const studyPage = document.querySelector('.study-page-final');
        if (studyPage) {
            observer.observe(studyPage, { attributes: true, attributeFilter: ['data-theme', 'class'] });
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (isVisible) {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                onCloseRef.current();
            }, duration);
        } else {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        }
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [isVisible, duration]);

    const handleOrganize = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (savedCardId) onOrganize(savedCardId);
        onCloseRef.current();
    };

    const handleClose = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        onCloseRef.current();
    };

    if (!isVisible) return null;

    return createPortal(
        <div
            className={`saved-toast ${isDark ? 'saved-toast--dark' : 'saved-toast--light'}`}
            role="status"
            aria-live="polite"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="saved-toast-top">
                <div className="saved-toast-icon">
                    <Bookmark size={14} fill="currentColor" />
                </div>
                <span className="saved-toast-text">Flashcard salvo</span>
                <button
                    className="saved-toast-btn-close"
                    onClick={handleClose}
                    aria-label="Fechar"
                >
                    <X size={13} />
                </button>
            </div>

            <button
                className="saved-toast-btn-organize"
                onClick={handleOrganize}
                title="Adicionar a uma pasta"
            >
                Organizar em pasta
            </button>
        </div>,
        document.body
    );
};

export default SavedToast;